/**
 * State and actions for the building assistant panel: which chat is open, sending a question,
 * feedback. Chat data itself comes live from useAssistantChat / useAssistantChatList.
 */
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { assistantApi } from '../../api/assistantApi';
import { buildingKeyFromPath } from '../../Helpers/assistantChatView';

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
}

export function useAssistantPanel() {
    const location = useLocation();
    const contextBuildingKey = buildingKeyFromPath(location.pathname);

    /** Open chat; a new chat has a key before it exists in the database. */
    const [chatKey, setChatKey] = useState<string | undefined>(undefined);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const openChat = useCallback((key: string) => {
        setError(null);
        setChatKey(key);
    }, []);

    const startNewChat = useCallback(() => {
        setError(null);
        setChatKey(assistantApi.newChatKey());
    }, []);

    const showList = useCallback(() => {
        setError(null);
        setChatKey(undefined);
    }, []);

    const ask = useCallback(async (question: string) => {
        if (!chatKey) return;
        setSending(true);
        setError(null);
        try {
            await assistantApi.ask({ chatKey, question, ...(contextBuildingKey ? { contextBuildingKey } : {}) });
        } catch (err) {
            console.error('Error asking assistant:', err);
            setError(errorMessage(err, 'Frågan kunde inte skickas'));
        } finally {
            setSending(false);
        }
    }, [chatKey, contextBuildingKey]);

    const submitFeedback = useCallback(
        async (turnKey: string, rating: 'up' | 'down', comment?: string): Promise<boolean> => {
            if (!chatKey) return false;
            try {
                await assistantApi.submitFeedback({ chatKey, turnKey, rating, ...(comment ? { comment } : {}) });
                return true;
            } catch (err) {
                console.error('Error submitting assistant feedback:', err);
                setError(errorMessage(err, 'Omdömet kunde inte sparas'));
                return false;
            }
        },
        [chatKey]
    );

    return { chatKey, contextBuildingKey, sending, error, openChat, startNewChat, showList, ask, submitFeedback };
}
